/* eslint-disable @typescript-eslint/no-explicit-any */
import { Environment } from 'environment.js'
import { download, fileUploadWithProgress, get, post, put, putETag, remove, removeETag } from 'Repository/index.js'
import type { Observable } from 'rxjs'
import { from } from 'rxjs'
import type {
  EItemType,
  FileForProcessing,
  IContentFile,
  ICustomerCreatedOrder,
  IOrder,
  IOrderWithCounts,
  ISamplePool,
  IUser,
  NegativeControl,
  PublicOrder,
  Sample
} from 'Model/index.js'
import type { IBulkResponse } from 'Framework/Repository/Response/IBulkResponse.js'
import type { IFileUploadLocation } from 'Model/File/IFileUploadLocation.js'
import type { SamplePoolLabel } from '../../Generated/SamplePoolLabel.ts'
import { tracingAxios } from '../Sentry.js'
import type { IPostprocessingInformation } from '../../Model/Order/IPostprocessingInformation.js'
import type { IPipelineState } from '../../Model/Order/IPipelineState.js'
import type { OrderProcessingData } from '../../Generated/OrderProcessingData.js'
import type { ISampleMetaData, NewSampleMetaData } from 'Model/Sample/ISampleMetaData.js'
import type { SampleConfigurationTypeKey } from '../../Generated/SampleConfigurationType.js'
import type { Taxas } from '../../Model/ITaxa.js'
import type { RedlistModeKey } from '../../Generated/RedlistMode.js'
import type { ProcessingTypeKey } from '../../Generated/ProcessingType.ts'
import type { OrderNotice } from '../../Generated/OrderNotice.ts'
import type { CreateOrderRequest } from '../../Generated/CreateOrderRequest.ts'
import type { ReportRequest } from '../../Generated/ReportRequest.js'
import type { SequencingPlatformKey } from '../../Generated/SequencingPlatform.ts'
import type { OrderIntermediatesStatus } from '../../Generated/OrderIntermediatesStatus.ts'
import type { ReprocessingGrantStatus } from '../../Generated/ReprocessingGrantStatus.ts'
import type { AttachReverseFileRequest } from '../../Generated/AttachReverseFileRequest.ts'

export const createOrder = (
  data: Omit<CreateOrderRequest, 'type'>,
  user?: IUser
): Observable<IOrder & { orderCreated: string }> =>
  post(`${Environment.API_URL}/api/orders`, { ...data, type: 'ORDER' } satisfies CreateOrderRequest, user)

export const createOrderDraft = (
  data: Omit<CreateOrderRequest, 'type'>,
  user?: IUser
): Observable<ICustomerCreatedOrder> =>
  post(`${Environment.API_URL}/api/orders`, { ...data, type: 'DRAFT' } satisfies CreateOrderRequest, user)

export const createSamplePool = (data: { orderId: string }, user?: IUser): Observable<any> => {
  return post(`${Environment.API_URL}/api/orders/${data.orderId}/pools`, {}, user)
}

export const deleteMetadata = (data: { orderId: string; metadataId: string }, user?: IUser): Observable<any> => {
  return remove(`${Environment.API_URL}/api/orders/${data.orderId}/sample-metadata/${data.metadataId}`, user)
}

export const deleteOrder = (data: { orderId: string }, user?: IUser): Observable<IOrder> => {
  return remove(`${Environment.API_URL}/api/orders/${data.orderId}`, user)
}

export const movePoolItems = (
  data: { orderId: string; sourcePoolId: string; targetPoolId: string; items: string[] },
  user?: IUser
): Observable<FileForProcessing[]> => {
  return post(`${Environment.API_URL}/api/orders/${data.orderId}/pools/${data.sourcePoolId}/move-to/${data.targetPoolId}`, data.items, user)
}

export const deletePoolItem = (data: {
  orderId: string
  poolId: string
  itemId: string
  version: string
}): Observable<FileForProcessing> => {
  return removeETag(`${Environment.API_URL}/api/orders/${data.orderId}/pools/${data.poolId}/contents/${data.itemId}`, data.version)
}

export const attachReverseFile = (data: {
  orderId: string
  poolId: string
  sampleId: string
  request: AttachReverseFileRequest
}): Observable<Sample | NegativeControl> => {
  return post(`${Environment.API_URL}/api/orders/${data.orderId}/pools/${data.poolId}/contents/${data.sampleId}/reverse-file`, data.request)
}

export const deleteSamplePool = (data: { orderId: string; poolId: string }, user?: IUser): Observable<void> => {
  return remove(`${Environment.API_URL}/api/orders/${data.orderId}/pools/${data.poolId}`, user)
}

export const updateSamplePoolLabel = (
  data: { orderId: string; poolId: string; label?: string; version: string },
  user?: IUser
): Observable<SamplePoolLabel> => {
  return putETag(
    `${Environment.API_URL}/api/orders/${data.orderId}/pools/${data.poolId}/label`,
    { label: data.label } satisfies SamplePoolLabel,
    data.version,
    user
  )
}

export const fetchPoolItem = (data: { orderId: string; poolId: string; itemId: string }): Observable<Sample | NegativeControl> => {
  return get(`${Environment.API_URL}/api/orders/${data.orderId}/pools/${data.poolId}/contents/${data.itemId}`)
}

export const getAllSamplePools = (data: { orderId: string }): Observable<ISamplePool[]> => {
  return get(`${Environment.API_URL}/api/orders/${data.orderId}/pools`)
}

export const getOrder = (orderId: string): Observable<IOrder> => {
  return get(`${Environment.API_URL}/api/orders/${orderId}`)
}

export const getOrderWithToken = (orderId: string, token: string): Observable<PublicOrder> => {
  return get(`${Environment.API_URL}/api/public/orders/${orderId}/${token}`)
}

export const getPostprocessingData = (orderId: string): Observable<IPostprocessingInformation> => {
  return get(`${Environment.API_URL}/api/orders/${orderId}/pipeline/data`)
}

export const getOtuProcessingData = (orderId: string): Observable<OrderProcessingData> => {
  return get(`${Environment.API_URL}/api/orders/${orderId}/pipeline/otu-processing-data`)
}

export const getPostprocessingState = (orderId: string): Observable<IPipelineState> => {
  return get(`${Environment.API_URL}/api/orders/${orderId}/pipeline/state`)
}
export const getOrderNotices = (orderId: string): Observable<OrderNotice[]> => {
  return get(`${Environment.API_URL}/api/orders/${orderId}/notices`)
}

export const getIntermediatesStatus = (orderId: string): Observable<OrderIntermediatesStatus> => {
  return get(`${Environment.API_URL}/api/orders/${orderId}/intermediates`)
}

export const getReprocessingGrant = (orderId: string): Observable<ReprocessingGrantStatus> => {
  return get(`${Environment.API_URL}/api/orders/${orderId}/reprocessing-grant`)
}

/** Discards the order's preprocessing results so a resubmit reprocesses every sample. */
export const resetOrderPreprocessing = (data: { orderId: string }, user?: IUser): Observable<IOrder> => {
  return get(`${Environment.API_URL}/api/orders/${data.orderId}/reset`, {}, user)
}

export const addSampleMetaDatas = (orderId: string, metaDatas: NewSampleMetaData[]): Observable<ISampleMetaData[]> => {
  return post(`${Environment.API_URL}/api/orders/${orderId}/sample-metadata`, metaDatas)
}
export const updateSampleMetaDatas = (orderId: string, metaDatas: ISampleMetaData[]): Observable<ISampleMetaData[]> => {
  return put(`${Environment.API_URL}/api/orders/${orderId}/sample-metadata/`, metaDatas)
}

export const getSampleMetaDatas = (orderId: string): Observable<ISampleMetaData[]> => {
  return get(`${Environment.API_URL}/api/orders/${orderId}/sample-metadata`)
}
export const getAllOrders = (
  data: { size: number; page: number; filters: { parameter: string; value: any; filterType: string }[] } | undefined,
  user?: IUser
): Observable<IBulkResponse<IOrderWithCounts>> => {
  //TODO: hier query filter einbinden
  return get(`${Environment.API_URL}/api/orders`, data, user)
}

export const requestFileUploadUrl = (
  orderId: string,
  poolId: string,
  data: { files: { fileName: string; encodedMd5: string; contentType: 'SAMPLE' }[] }
): Observable<IFileUploadLocation[]> => {
  return post(`${Environment.API_URL}/api/orders/${orderId}/pools/${poolId}`, data)
}

export const setContent = (
  orderId: string,
  poolId: string,
  data: {
    itemType: EItemType
    forwardFile?: IContentFile
    forwardFileId: string
    forwardPrimer?: string
    reverseFile?: IContentFile
    reverseFileId?: string
    reversePrimer?: string
    customerSampleName?: string
    primerLiterature?: string
    isTrimmed: boolean
    isMerged: boolean
  }
): Observable<Sample | NegativeControl> => {
  return post(`${Environment.API_URL}/api/orders/${orderId}/pools/${poolId}/contents`, data)
}

export const updatePoolContent = (
  orderId: string,
  poolId: string,
  data: {
    itemType: EItemType
    forwardFile?: IContentFile
    forwardFileId: string
    forwardPrimer?: string
    reverseFile?: IContentFile
    reverseFileId?: string
    reversePrimer?: string
    customerSampleName?: string
    primerLiterature?: string
  }[]
): Observable<Sample[]> => {
  return put(`${Environment.API_URL}/api/orders/${orderId}/pools/${poolId}/contents`, data)
}

export const uploadSample = (data: { orderId: string; samplepoolId: string; forwardFile: File; reverseFile: File }): Observable<any> => {
  return fileUploadWithProgress(`${Environment.API_URL}/api/order/${data.orderId}/pools/${data.samplepoolId}/samples`, data /*user*/)
}

export const uploadNegativeControls = (data: {
  orderId: string
  datasetId: string
  forwardFile: File
  reverseFile: File
}): Observable<any> => {
  return fileUploadWithProgress(
    `${Environment.API_URL}/v1/orders/${data.orderId}/data-sets/${data.datasetId}/negative-controls`,
    data /*user*/
  )
}

export const updateNegativeControl = (
  data: {
    orderId: string
    datasetId: string
    negativeControlId: string
    customerSampleId: string
    forwardPrimer: string
    reversePrimer: string
    minBasePairLength: number
  },
  user?: IUser
): Observable<{ resource: ISamplePool }> => {
  return put(`${Environment.API_URL}/v1/upload/data-set/${data.datasetId}/negative-control/${data.negativeControlId}`, data, user)
}

// export const updateSample = (
//   data: {
//     orderId: string,
//     datasetId: string
//     sampleId: string
//     customerSampleId: string,
//     forwardPrimer: string,
//     reversePrimer: string,
//     minBasePairLength:number
//   },
//   user?: IUser,
// ): Observable<{ resource: ISamplePool }> => {
//   return put(`${Environment.API_URL}/v1/upload/data-set/${data.datasetId}/sample/${data.sampleId}`, data, user)
// }

export const claimOrder = (
  orderId: string,
  data: {
    targetTaxonomy?: string
    targetRegion?: string | null
    allowSingletons?: boolean
    sampleConfiguration?: SampleConfigurationTypeKey
    redlistMode?: RedlistModeKey
  },
  user?: IUser,
  token?: string
): Observable<IOrder> => {
  const payload = { ...data } as any
  if (payload.targetRegion === undefined) delete payload.targetRegion
  return post(`${Environment.API_URL}/api/orders/${orderId}/claim/${token ? token : ''}`, payload, user)
}

export const submitOrder = (
  data: {
    orderId: string
  },
  user?: IUser
): Observable<IOrder> => {
  return get(`${Environment.API_URL}/api/orders/${data.orderId}/submit`, {}, user)
}

export const forceSubmitOrder = (
  data: {
    orderId: string
    excludedItems: string[]
  },
  user?: IUser
): Observable<IOrder> => {
  return post(`${Environment.API_URL}/api/orders/${data.orderId}/forceSubmit`, { excludedItems: data.excludedItems }, user)
}

export const pullbackOrder = (
  data: {
    orderId: string
  },
  user?: IUser
): Observable<IOrder> => {
  return get(`${Environment.API_URL}/api/orders/${data.orderId}/pull-back`, {}, user)
}

export const getDatasetReport = (
  data: {
    orderId: string
  },
  user?: IUser
): Observable<Blob> => {
  return download(`${Environment.API_URL}/v1/upload/${data.orderId}/data-set/result`, data, user)
}

export const getOrderFiles = (data: { orderId: string; fileId: string }): Observable<Blob> => {
  return download(`${Environment.API_URL}/v1/upload/${data.orderId}/data-set/files/${data.fileId}`, data)
}

export const updateOrderName = (data: {
  orderId: string
  version: string
  name: string
  description?: string | null
  taxa: Taxas
  targetRegion?: string | null
  allowSingletons: boolean
  processingType: ProcessingTypeKey
  redlistMode: RedlistModeKey
  sampleConfiguration?: SampleConfigurationTypeKey
}): Observable<IOrder> => {
  const payload: any = {
    name: data.name,
    description: data.description ?? undefined,
    targetTaxonomy: data.taxa,
    allowSingletons: data.allowSingletons,
    processingType: data.processingType,
    version: data.version,
    targetRegion: data.targetRegion ?? undefined,
    redlistMode: data.redlistMode,
    sampleConfiguration: data.sampleConfiguration ?? undefined
  }
  if (payload.description === undefined) delete payload.description
  if (payload.targetRegion === undefined) delete payload.targetRegion
  if (payload.sampleConfiguration === undefined) delete payload.sampleConfiguration
  return put(`${Environment.API_URL}/api/orders/${data.orderId}/`, payload)
}

/** `platform: null` clears a previous override back to the detected value. */
export const overrideSequencingPlatform = (data: {
  orderId: string
  platform: SequencingPlatformKey | null
  version: string
}): Observable<IOrder> => {
  return put(`${Environment.API_URL}/api/orders/${data.orderId}/sequencing-platform`, { platform: data.platform, version: data.version })
}

export const downloadFastq = (orderId: string) =>
  from(
    tracingAxios.get<{ link: string }>(`${Environment.API_URL}/api/orders/${orderId}/fastq`, {
      withCredentials: true
    })
  )

/** `path` is the artifact's endpoint segment — see `orderArtifactDescriptors`. Answers `410` when absent. */
export const downloadOrderArtifact = (orderId: string, path: string) =>
  from(
    tracingAxios.get<{ link: string }>(`${Environment.API_URL}/api/orders/${orderId}/${path}`, {
      withCredentials: true
    })
  )

/**
 * Poll for an order report, generating it if it is not there yet (202 → keep polling, 200 → link).
 *
 * `settings` switches to the POST sibling, which is the only variant that can carry the SPIN knobs. The
 * anonymous checksum link stays on the plain GET deliberately: a shared link serves the generic report, not
 * whatever settings the sharer happened to pick.
 */
export const downloadReport = (orderId: string, checksum?: string, settings?: ReportRequest) => {
  if (checksum) {
    return from(
      tracingAxios.get<{ link: string }>(`${Environment.API_URL}/api/public/orders/${orderId}/report/${checksum}`, {
        withCredentials: true
      })
    )
  } else if (settings) {
    return from(
      tracingAxios.post<{ link: string }>(`${Environment.API_URL}/api/orders/${orderId}/report`, settings, {
        withCredentials: true
      })
    )
  } else {
    return from(
      tracingAxios.get<{ link: string }>(`${Environment.API_URL}/api/orders/${orderId}/report`, {
        withCredentials: true
      })
    )
  }
}

export const getOtuCount = (orderId: string): Observable<number> => {
  return get(`${Environment.API_URL}/api/orders/${orderId}/otu/count`)
}

export const submitPostprocessingToken = (token: string) => {
  return from(
    tracingAxios.post<{ status: string; orderId: string | null }>(
      `${Environment.API_URL}/api/public/action-tokens/postprocessing/submit`,
      { token },
      { withCredentials: true }
    )
  )
}

export const getFiles = (orderId: string): Observable<{ file: IContentFile; samples?: FileForProcessing[] }[]> => {
  return get(`${Environment.API_URL}/api/orders/${orderId}/files/`)
}
